import { styled } from 'solid-styled-components';
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  SHADOWS,
  TYPOGRAPHY,
  ANIMATION,
} from '../../../constants/design-tokens';

// Button variants and sizes
const buttonVariants = {
  primary: {
    backgroundColor: COLORS.primary[500],
    color: COLORS.text.inverse,
    '&:hover': {
      backgroundColor: COLORS.primary[600],
    },
    '&:active': {
      backgroundColor: COLORS.primary[700],
    },
  },

  secondary: {
    backgroundColor: 'transparent',
    color: COLORS.phosphor.greenDim,
    border: `1px solid ${COLORS.phosphor.greenMute}`,
    '&:hover': {
      backgroundColor: 'rgba(198, 232, 154, 0.08)',
    },
    '&:active': {
      backgroundColor: 'rgba(198, 232, 154, 0.16)',
    },
  },

  danger: {
    backgroundColor: COLORS.error[500],
    color: COLORS.text.inverse,
    '&:hover': {
      backgroundColor: COLORS.error[600],
    },
    '&:active': {
      backgroundColor: COLORS.error[700],
    },
  },

  ghost: {
    backgroundColor: 'transparent',
    color: COLORS.phosphor.green,
    '&:hover': {
      backgroundColor: 'rgba(198, 232, 154, 0.12)',
    },
    '&:active': {
      backgroundColor: 'rgba(198, 232, 154, 0.2)',
    },
  },
};

const buttonSizes = {
  sm: {
    padding: `${SPACING[2]} ${SPACING[3]}`,
    fontSize: TYPOGRAPHY.fontSize.sm,
    minHeight: '32px',
  },

  md: {
    padding: `${SPACING[3]} ${SPACING[4]}`,
    fontSize: TYPOGRAPHY.fontSize.base,
    minHeight: '40px',
  },

  lg: {
    padding: `${SPACING[4]} ${SPACING[6]}`,
    fontSize: TYPOGRAPHY.fontSize.lg,
    minHeight: '48px',
  },
};

const StyledButton = styled.button`
  /* Base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING[2]};
  border: none;
  border-radius: ${BORDER_RADIUS.md};
  font-family: ${TYPOGRAPHY.fontFamily.mono.join(', ')};
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  line-height: ${TYPOGRAPHY.lineHeight.tight};
  cursor: pointer;
  transition: all ${ANIMATION.duration.base} ${ANIMATION.easing.easeInOut};
  box-shadow: none;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  border-radius: ${BORDER_RADIUS.base};

  /* Focus styles for accessibility */
  &:focus {
    outline: none;
    box-shadow:
      0 0 0 2px ${COLORS.primary[500]},
      ${SHADOWS.sm};
  }

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Loading state */
  &[data-loading='true'] {
    opacity: 0.7;
    cursor: wait;
    pointer-events: none;
  }

  /* Size variants */
  ${(props) => buttonSizes[props.size] || buttonSizes.md}

  /* Style variants */
  ${(props) => buttonVariants[props.variant] || buttonVariants.primary}
  
  /* Full width */
  ${(props) => props.fullWidth && 'width: 100%;'}
  
  /* Icon-only button */
  ${(props) =>
    props.iconOnly &&
    `
    padding: ${SPACING[3]};
    min-width: auto;
    aspect-ratio: 1;
  `}
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Professional Button component with multiple variants, sizes, and states
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant: 'primary' | 'secondary' | 'danger' | 'ghost'
 * @param {string} props.size - Button size: 'sm' | 'md' | 'lg'
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.fullWidth - Full width button
 * @param {boolean} props.iconOnly - Icon-only button (square aspect ratio)
 * @param {JSX.Element} props.leftIcon - Icon on the left side
 * @param {JSX.Element} props.rightIcon - Icon on the right side
 * @param {Function} props.onClick - Click handler
 * @param {string} props.children - Button content
 * @param {string} props.ariaLabel - Accessibility label
 * @param {string} props.title - Tooltip text
 */
const Button = (props) => {
  const {
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    fullWidth = false,
    iconOnly = false,
    leftIcon,
    rightIcon,
    onClick,
    children,
    ariaLabel,
    title,
    ...restProps
  } = props;

  const handleClick = (event) => {
    if (disabled || loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <StyledButton
      variant={variant}
      size={size}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      iconOnly={iconOnly}
      data-loading={loading}
      onClick={handleClick}
      aria-label={ariaLabel}
      title={title}
      {...restProps}
    >
      {loading && <LoadingSpinner />}
      {!loading && leftIcon && leftIcon}
      {!iconOnly && children}
      {!loading && rightIcon && rightIcon}
    </StyledButton>
  );
};

export default Button;
